/**
 * THE SUBMISSION WIZARD (UF-05, FR-U-TOK-001).
 *
 * ## What this used to be, and why it had to change
 *
 * A six-step form with the property fields hardcoded into it, a media step whose "Add" button pushed
 * the string `"exterior-1"` into an array, and a documents step whose "Attach" button set a boolean.
 * Nothing was ever picked, uploaded, hashed or stored. The review step then counted the booleans and
 * reported "3 attached", and the asset went to the trust with no title deed on it.
 *
 * Two things are different now, and both are structural rather than cosmetic:
 *
 *   1. THE FORM IS GENERATED. The fields, their units, their help text and the documents required all
 *      come from the tokenization product (`GET /tokenize/lifecycle/products/{code}`, migration 0055).
 *      A receivable is never asked for its construction year, and adding a product is an INSERT.
 *
 *   2. THE UPLOADS ARE REAL. `DocumentSlot` opens the device file picker, sends multipart with
 *      progress, and shows the hash the SERVER computed over the bytes.
 *
 * ## The application exists before the documents do
 *
 * Step 1 and 2 collect the answers; then the application is CREATED, in draft, and the documents are
 * attached to it. That order is forced by the architecture rather than chosen: a document is evidence
 * ABOUT an asset, `asset_document` carries the application it was submitted for, and there is nowhere
 * to put a file that belongs to an application nobody has started.
 *
 * It is also better for the owner. A draft survives a closed tab, so somebody who has to go and find
 * their rates clearance certificate comes back to the answers they already gave.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { TierGate } from '@/components/gate';
import { Reveal } from '@/components/motion';
import { ErrorState, LoadingCard } from '@/components/states';
import { DocumentSlot, useUploadPolicy } from '@/components/upload';
import {
  api,
  LipaApiError,
  type ProductConfig,
  type TokenizationApplication,
  type TokenizationProduct,
  type ProductField,
} from '@/lib/api';
import { docLabel } from '@/lib/tokenization';

const STEPS = ['Product', 'Asset', 'Ownership', 'Tokenization', 'Documents', 'Review'];

export default function NewSubmission() {
  return (
    <TierGate requires="T2" what="Submitting an asset for tokenization">
      <Wizard />
    </TierGate>
  );
}

function Wizard() {
  const navigate = useNavigate();
  const policy = useUploadPolicy();

  const [products, setProducts] = useState<TokenizationProduct[] | null>(null);
  const [config, setConfig] = useState<ProductConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [reference, setReference] = useState('');

  /** Set once the draft exists. From then on the wizard is editing a real application. */
  const [application, setApplication] = useState<TokenizationApplication | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ reference: string; id: string } | null>(null);

  useEffect(() => {
    api.tokenization
      .products()
      .then(setProducts)
      .catch((e) =>
        setLoadError(
          e instanceof LipaApiError
            ? e.detail.message
            : 'We could not load the tokenization products.',
        ),
      );
  }, []);

  const sections = useMemo(() => {
    if (!config) return { asset: [], ownership: [], tokenization: [] };
    return {
      asset: config.fields.filter((f) => f.section === 'asset'),
      ownership: config.fields.filter((f) => f.section === 'ownership'),
      tokenization: config.fields.filter((f) => f.section === 'tokenization'),
    };
  }, [config]);

  const refresh = useCallback(async () => {
    if (!application) return;
    setApplication(await api.tokenization.application(application.application_id));
  }, [application]);

  async function pickProduct(code: string) {
    setError(null);
    setBusy(true);
    try {
      setConfig(await api.tokenization.product(code));
      setAnswers({});
      setStep(1);
    } catch (e) {
      setError(describe(e, 'We could not load that product.'));
    } finally {
      setBusy(false);
    }
  }

  /** Create the draft, so the documents step has an application to attach to. */
  async function startDraft() {
    if (!config) return;
    setBusy(true);
    setError(null);
    try {
      const started = await api.tokenization.start({
        product_code: config.code,
        ownership_reference: reference.trim(),
        details: answers,
      });
      setApplication(await api.tokenization.application(started.application_id));
      setStep(4);
    } catch (e) {
      setError(describe(e, 'We could not start this application.'));
    } finally {
      setBusy(false);
    }
  }

  async function saveAndContinue() {
    if (!application) return;
    setBusy(true);
    setError(null);
    try {
      await api.tokenization.update(application.application_id, { details: answers });
      await refresh();
      setStep(5);
    } catch (e) {
      setError(describe(e, 'We could not save your answers.'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Submit. The server refuses while anything the product requires is missing, and the message names
   * what. That refusal is the control; the disabled-button logic below is only a courtesy.
   */
  async function submit() {
    if (!application) return;
    setBusy(true);
    setError(null);
    try {
      await api.tokenization.submit(application.application_id);
      setDone({ reference: application.reference, id: application.application_id });
    } catch (e) {
      setError(describe(e, 'We could not submit this application.'));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <ErrorState body={loadError} />;
  if (!products) return <LoadingCard lines={4} />;
  if (done)
    return (
      <Submitted
        reference={done.reference}
        onOpen={() => navigate(`/tokenize/applications/${done.id}`)}
      />
    );

  const missingRequired = (fields: ProductField[]) =>
    fields.filter((f) => f.required && !String(answers[f.key] ?? '').trim());

  const outstanding = application?.checklist.filter((c) => !c.satisfied) ?? [];

  const canContinue =
    (step === 0 && !!config) ||
    (step === 1 && missingRequired(sections.asset).length === 0 && reference.trim().length > 0) ||
    (step === 2 && missingRequired(sections.ownership).length === 0) ||
    (step === 3 && missingRequired(sections.tokenization).length === 0) ||
    step === 4 ||
    step === 5;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/tokenize"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Tokenize
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Submit an asset for tokenization</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          You submit. The platform reviews, a regulated trust approves it for valuation, an
          independent valuer values it, and you are then offered terms to agree to before any token
          exists.
        </p>
      </header>

      <Stepper step={step} />

      <Reveal key={step}>
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          {step === 0 ? (
            <StepProduct products={products} selected={config?.code ?? null} onPick={pickProduct} />
          ) : null}

          {step === 1 && config ? (
            <StepFields
              title={`${config.name}: the asset`}
              blurb="What you are tokenizing. The valuer will check every one of these against the register and the site."
              fields={sections.asset}
              answers={answers}
              onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))}
              extra={
                <Field
                  label={referenceLabel(config.asset_class)}
                  hint="The natural key of the real-world asset. This is what the register is searched on, and what makes sure one asset is tokenized once."
                  required
                >
                  <TextInput value={reference} onChange={setReference} />
                </Field>
              }
            />
          ) : null}

          {step === 2 && config ? (
            <StepFields
              title="Ownership"
              blurb="How you hold it, and anything already secured on it. An undisclosed charge is found by the register search, and found late."
              fields={sections.ownership}
              answers={answers}
              onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))}
            />
          ) : null}

          {step === 3 && config ? (
            <StepFields
              title="How you would like it tokenized"
              blurb="A proposal, not an instruction. The offer you are sent is generated from the approved valuation and may differ, and that difference is exactly what you will be asked to agree to."
              fields={sections.tokenization}
              answers={answers}
              onChange={(k, v) => setAnswers((a) => ({ ...a, [k]: v }))}
            />
          ) : null}

          {step === 4 && config && application && policy ? (
            <StepDocuments
              config={config}
              application={application}
              accept={policy.accept}
              maxBytes={policy.max_bytes}
              onChanged={refresh}
            />
          ) : null}

          {step === 4 && (!application || !policy) ? <LoadingCard lines={3} /> : null}

          {step === 5 && config && application ? (
            <StepReview config={config} application={application} answers={answers} />
          ) : null}
        </section>
      </Reveal>

      {error ? (
        <p className="rounded-field bg-danger/10 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => (step === 0 ? navigate('/tokenize') : setStep((s) => s - 1))}
          className="rounded-field border border-border px-4 py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Back
        </button>

        {step === 0 ? null : step === 3 ? (
          <button
            onClick={() => void startDraft()}
            disabled={!canContinue || busy}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Saving...' : 'Save and add documents'}
          </button>
        ) : step === 4 ? (
          <button
            onClick={() => void saveAndContinue()}
            disabled={busy}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
          >
            Review
          </button>
        ) : step === 5 ? (
          <button
            onClick={() => void submit()}
            disabled={busy || outstanding.length > 0}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy ? 'Submitting...' : 'Submit'}
          </button>
        ) : (
          <button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canContinue}
            className="rounded-field bg-primary px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-40"
          >
            Continue
          </button>
        )}
      </div>

      {application ? (
        <p className="text-center text-micro text-ink-subtle">
          Saved as a draft, {application.reference}. You can close this and come back to it.
        </p>
      ) : null}
    </div>
  );
}

// ---- Steps ---------------------------------------------------------------------------------------

function StepProduct({
  products,
  selected,
  onPick,
}: {
  products: TokenizationProduct[];
  selected: string | null;
  onPick: (code: string) => void;
}) {
  return (
    <div>
      <h2 className="text-section text-ink">What are you tokenizing?</h2>
      <p className="mt-1 text-sm text-ink-muted">
        The product decides what information and which documents are needed, and how the asset is
        valued.
      </p>
      <div className="mt-4 space-y-2.5">
        {products.map((p) => (
          <button
            key={p.code}
            onClick={() => onPick(p.code)}
            className={`w-full rounded-card border p-3.5 text-left transition ${
              selected === p.code
                ? 'border-primary bg-primary-tint'
                : 'border-border bg-surface hover:border-primary/40'
            }`}
          >
            <p className="text-sm font-medium text-ink">{p.name}</p>
            <p className="mt-0.5 text-micro text-ink-subtle">{p.summary}</p>
            {/* What the holder would actually own. Said here rather than at the end, because it is
                the thing that decides whether this is the right product at all. */}
            <p className="mt-1.5 text-nano text-ink-subtle">
              Structure: {p.structure} &middot; classified as {p.classification.replace('_', ' ')}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepFields({
  title,
  blurb,
  fields,
  answers,
  onChange,
  extra,
}: {
  title: string;
  blurb: string;
  fields: ProductField[];
  answers: Record<string, string>;
  onChange: (key: string, value: string) => void;
  extra?: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="text-section text-ink">{title}</h2>
      <p className="mt-1 text-sm text-ink-muted">{blurb}</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {extra ? <div className="sm:col-span-2">{extra}</div> : null}
        {fields.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            hint={f.help ?? undefined}
            unit={f.unit}
            required={f.required}
            className={f.kind === 'long_text' ? 'sm:col-span-2' : ''}
          >
            <Input field={f} value={answers[f.key] ?? ''} onChange={(v) => onChange(f.key, v)} />
          </Field>
        ))}
      </div>
    </div>
  );
}

function StepDocuments({
  config,
  application,
  accept,
  maxBytes,
  onChanged,
}: {
  config: ProductConfig;
  application: TokenizationApplication;
  accept: string[];
  maxBytes: number;
  onChanged: () => Promise<void>;
}) {
  const outstanding = application.checklist.filter((c) => !c.satisfied);

  return (
    <div>
      <h2 className="text-section text-ink">Documents</h2>
      <p className="mt-1 text-sm text-ink-muted">
        What {config.name.toLowerCase()} needs. We hash every file as it arrives, so a document
        cannot be swapped afterwards, and nothing you upload is ever deleted.
      </p>

      <div className="mt-3 rounded-field bg-surface-sunken px-3 py-2">
        <p className="text-micro text-ink-muted">
          {outstanding.length === 0
            ? 'Everything required is provided.'
            : `Still needed: ${outstanding.map((c) => docLabel(c.doc_type)).join(', ')}.`}
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {config.documents.map((requirement) => (
          <DocumentSlot
            key={requirement.doc_type}
            applicationId={application.application_id}
            docType={requirement.doc_type}
            label={docLabel(requirement.doc_type)}
            note={requirement.note}
            required={requirement.required}
            minCount={requirement.min_count}
            documents={application.documents}
            accept={accept}
            maxBytes={maxBytes}
            onChanged={onChanged}
          />
        ))}
      </div>
    </div>
  );
}

function StepReview({
  config,
  application,
  answers,
}: {
  config: ProductConfig;
  application: TokenizationApplication;
  answers: Record<string, string>;
}) {
  const outstanding = application.checklist.filter((c) => !c.satisfied);

  return (
    <div>
      <h2 className="text-section text-ink">Review and submit</h2>
      <p className="mt-1 text-sm text-ink-muted">
        This goes to the platform first. You will see every stage from your dashboard.
      </p>

      <dl className="mt-4 divide-y divide-border rounded-card border border-border">
        <Row k="Product" v={config.name} />
        <Row k="Reference" v={application.asset.ownership_reference} />
        {config.fields
          .filter((f) => (answers[f.key] ?? '').trim())
          .map((f) => (
            <Row key={f.key} k={f.label} v={`${answers[f.key]}${f.unit ? ` ${f.unit}` : ''}`} />
          ))}
        <Row k="Documents" v={`${application.documents.length} uploaded`} />
      </dl>

      {outstanding.length > 0 ? (
        <p className="mt-3 rounded-field bg-danger/10 px-3 py-2 text-micro text-danger">
          Still needed before this can be submitted:{' '}
          {outstanding.map((c) => docLabel(c.doc_type)).join(', ')}.
        </p>
      ) : null}

      <p className="mt-3 rounded-field bg-primary-tint px-3 py-2 text-micro text-ink">
        Submitting does not create tokens and does not commit you to anything. The trust values the
        asset, and you will be sent an offer with the token count, the price and the terms. Nothing
        is issued until you have agreed to it.
      </p>
    </div>
  );
}

function Submitted({ reference, onOpen }: { reference: string; onOpen: () => void }) {
  return (
    <div className="mx-auto max-w-md space-y-6 py-6 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/15 text-accent">
        <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m5 13 4 4L19 7" />
        </svg>
      </div>
      <div>
        <h1 className="text-page-title text-ink">Submitted</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Your reference is <span className="font-mono text-ink">{reference}</span>. Quote it if you
          contact support.
        </p>
      </div>
      <button
        onClick={onOpen}
        className="w-full rounded-field bg-primary py-3 text-sm font-medium text-white transition hover:bg-primary-light"
      >
        Track it
      </button>
    </div>
  );
}

// ---- Primitives ----------------------------------------------------------------------------------

function Stepper({ step }: { step: number }) {
  return (
    <ol className="flex items-center gap-1.5" aria-label="Submission progress">
      {STEPS.map((label, i) => (
        <li key={label} className="flex flex-1 flex-col gap-1">
          <span className={`h-1 rounded-full ${i <= step ? 'bg-primary' : 'bg-surface-sunken'}`} />
          <span
            className={`text-nano ${i === step ? 'font-semibold text-ink' : 'text-ink-subtle'}`}
          >
            {label}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** One input, chosen by the field's declared KIND. The unit is shown, never assumed. */
function Input({
  field,
  value,
  onChange,
}: {
  field: ProductField;
  value: string;
  onChange: (v: string) => void;
}) {
  if (field.kind === 'select') {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      >
        <option value="">Choose...</option>
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  if (field.kind === 'long_text') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
    );
  }

  if (field.kind === 'money') {
    return (
      <div className="flex h-11 items-center rounded-field border border-border bg-surface px-3 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <span className="mr-2 text-sm text-ink-subtle">{field.unit ?? 'KES'}</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          placeholder="0"
          className="tabular h-full w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-subtle"
        />
      </div>
    );
  }

  if (field.kind === 'date') {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
    );
  }

  const numeric = ['number', 'area', 'rate', 'years'].includes(field.kind);
  return <TextInput value={value} onChange={onChange} inputMode={numeric ? 'numeric' : 'text'} />;
}

function TextInput({
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className="h-11 w-full rounded-field border border-border bg-surface px-3 text-base text-ink outline-none transition placeholder:text-ink-subtle focus:border-primary focus:ring-2 focus:ring-primary/20"
    />
  );
}

function Field({
  label,
  hint,
  unit,
  required,
  className,
  children,
}: {
  label: string;
  hint?: string;
  unit?: string | null;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="text-sm font-medium text-ink">{label}</span>
      {unit && unit !== 'KES' ? (
        <span className="ml-1.5 text-micro text-ink-subtle">({unit})</span>
      ) : null}
      {required ? <span className="ml-1.5 text-micro text-danger">Required</span> : null}
      {hint ? <span className="mt-0.5 block text-micro text-ink-subtle">{hint}</span> : null}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-start justify-between gap-3 px-4 py-2.5">
      <dt className="text-sm text-ink-muted">{k}</dt>
      <dd className="tabular max-w-[60%] text-right text-sm font-medium text-ink">{v}</dd>
    </div>
  );
}

/** What the natural key of this asset class is CALLED, so the field is not labelled "reference". */
function referenceLabel(assetClass: string): string {
  return (
    {
      real_estate: 'Title number',
      land: 'Title number',
      vehicle: 'Registration number',
      receivable: 'Invoice or contract reference',
      inventory: 'Warehouse receipt number',
    }[assetClass] ?? 'Ownership reference'
  );
}

function describe(e: unknown, fallback: string): string {
  return e instanceof LipaApiError ? e.detail.message : fallback;
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
